import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/**
 * /contact — dedicated contact page.
 * Premium engineered aesthetic, Apple / Linear / Stripe level polish.
 * Three glass cards: HELLO, CONTACT, FOUNDER.
 */

const EASE = [0.16, 1, 0.3, 1];

const CARDS = [
  {
    eyebrow: "Hello",
    email: "hello@parabolum.digital",
    items: ["Ideas", "Partnerships", "New projects"],
    testid: "contact-card-hello",
  },
  {
    eyebrow: "Contact",
    email: "contact@parabolum.digital",
    items: ["General inquiries", "Questions", "Support"],
    testid: "contact-card-contact",
  },
  {
    eyebrow: "Founder",
    email: "jiri.mican@parabolum.digital",
    items: ["Founder", "Jiří Mičán", "PARABOLUM"],
    testid: "contact-card-founder",
  },
];

export default function ContactPage() {
  useEffect(() => {
    document.title = "Contact — Parabolum";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="contact-page"
      className="relative min-h-screen overflow-hidden bg-[#050507] text-white"
    >
      {/* very faint engineered grid */}
      <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.04]" />

      {/* tiny calm stars */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 36 }).map((_, i) => {
          const left = (i * 37) % 100;
          const top = ((i * 53) % 100);
          const size = i % 7 === 0 ? 2 : 1;
          const opacity = 0.08 + ((i * 13) % 18) / 100;
          return (
            <span
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${size}px`,
                height: `${size}px`,
                opacity,
              }}
            />
          );
        })}
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-[1400px] flex-col px-6 pb-24 pt-28 md:px-12 md:pb-32 md:pt-36">
        {/* back link */}
        <div className="mb-16 md:mb-24">
          <Link
            to="/"
            data-testid="contact-back-link"
            data-cursor="hover"
            className="group inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.28em] text-white/45 transition-colors hover:text-white"
          >
            <span className="inline-block transition-transform duration-500 group-hover:-translate-x-1">
              ←
            </span>
            Parabolum
          </Link>
        </div>

        {/* hero */}
        <header className="mb-20 max-w-3xl md:mb-28">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-7 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-white/30" />
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-white/50">
              Contact
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
            className="font-display text-5xl font-bold leading-[1] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.4rem]"
          >
            Let&apos;s build something{" "}
            <span className="font-serif-italic font-normal text-white/55">
              exceptional.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="font-body mt-7 max-w-xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            Have an idea, project or question?
            <br />
            We&apos;d love to hear from you.
          </motion.p>
        </header>

        {/* cards */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } },
          }}
          className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5"
        >
          {CARDS.map((c) => (
            <motion.a
              key={c.testid}
              href={`mailto:${c.email}`}
              data-cursor="hover"
              data-testid={c.testid}
              variants={{
                hidden: { opacity: 0, y: 18 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
              }}
              className="group relative flex h-full flex-col gap-8 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-7 backdrop-blur-[2px] transition-all duration-500 hover:border-white/[0.16] hover:bg-white/[0.035] focus:outline-none focus-visible:border-white/30 md:p-8"
            >
              {/* top: eyebrow + corner arrow */}
              <div className="flex items-start justify-between">
                <span className="font-body text-[11px] uppercase tracking-[0.32em] text-white/50">
                  {c.eyebrow}
                </span>
                <span className="grid h-8 w-8 place-items-center rounded-full border border-white/[0.08] text-white/45 transition-all duration-500 group-hover:border-white/30 group-hover:text-white">
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </span>
              </div>

              {/* middle: email */}
              <div className="font-display text-[19px] tracking-tight text-white transition-colors duration-500 group-hover:text-white md:text-[22px]">
                {c.email}
              </div>

              {/* bottom: items */}
              <ul className="flex flex-col gap-2 border-t border-white/[0.05] pt-5">
                {c.items.map((it) => (
                  <li
                    key={it}
                    className="font-body text-[13px] leading-relaxed text-white/55"
                  >
                    {it}
                  </li>
                ))}
              </ul>
            </motion.a>
          ))}
        </motion.div>

        {/* response time block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
          className="mt-20 grid items-start gap-8 border-t border-white/[0.06] pt-12 md:mt-28 md:grid-cols-12"
        >
          <div className="md:col-span-4">
            <span className="font-body text-[11px] uppercase tracking-[0.32em] text-white/50">
              Response time
            </span>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-2xl tracking-tight text-white md:text-3xl">
              Usually within 24 hours.
            </p>
            <p className="font-serif-italic mt-2 text-[15px] text-white/45">
              Sometimes much sooner.
            </p>
          </div>
        </motion.div>

        {/* footnote */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
          className="font-serif-italic mt-16 max-w-2xl text-[15px] text-white/40 md:mt-24"
        >
          Direct communication. Small studio. We read every message.
        </motion.p>
      </div>
    </main>
  );
}
